import type { MutableRefObject } from 'react';
import { useEffect, useRef, useState } from 'react';

export type ChildNodesIntersection = { target: Element; isIntersecting: boolean } | undefined;

type UseChildNodesIntersection = (
  parentNode?: Element | null,
  childNodes?: NodeListOf<Element> | null,
) => {
  isFirstNodeIntersected: boolean;
  isLastNodeIntersected: boolean;
  records: MutableRefObject<Record<string, ChildNodesIntersection> | undefined>;
};

const useChildNodesIntersection: UseChildNodesIntersection = (parentNode, childNodes) => {
  const childNodesIntersectionRecord = useRef<Record<string, ChildNodesIntersection>>();
  const [childNodesIntersection, setChildNodesIntersection] = useState({
    isFirstNodeIntersected: false,
    isLastNodeIntersected: true,
  });

  useEffect(() => {
    if (!parentNode || !childNodes) {
      return;
    }

    const options = {
      root: parentNode,
      rootMargin: '1px',
      threshold: 1,
    };

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        childNodesIntersectionRecord.current = {
          ...childNodesIntersectionRecord.current,
          [entry.target.id]: {
            target: entry.target,
            isIntersecting: entry.isIntersecting,
          },
        };

        if (entry.target === childNodes[0]) {
          // * first node is not visible anymore
          setChildNodesIntersection((state) => ({
            ...state,
            isFirstNodeIntersected: !entry.isIntersecting,
          }));
        } else if (entry.target === childNodes[childNodes.length - 1]) {
          // * last node is not visible anymore
          setChildNodesIntersection((state) => ({
            ...state,
            isLastNodeIntersected: !entry.isIntersecting,
          }));
        }
      }
    }, options);

    // * observe all childNodes
    childNodes.forEach(node => observer.observe(node));

    return () => {
      observer.disconnect();
    };
  }, [childNodes, parentNode]);

  return {
    ...childNodesIntersection,
    records: childNodesIntersectionRecord,
  };
};

export default useChildNodesIntersection;
